import React, { useEffect, useState, useCallback } from "react";
import { Grid, TextField, Autocomplete } from "@mui/material";
import { DateRangePicker } from "rsuite";
import AdvancedTable from "../../sharedComponents/AdvancedTable/AdvancedTable";
import { getUserReport } from "../../services/report.service";
import { getUser } from "../../services/user.service";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { ISSUETYPE } from "../../common/constant/constant";
import ClientTaskDetailsDialog from "./Components/ClientTaskDetailsDialog";

const { RangePicker } = DatePicker;

const CandidateReportTable = () => {
  const [data, setData] = useState([]);
  const [projectNames, setProjectNames] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    projectId: null,
    projectName: "",
    userId: null,
    issueType: null,
    startDate: null,
    endDate: null,
  });

  const initialMonth = dayjs();
  const [dateRange, setDateRange] = useState({
    startDate: initialMonth.startOf("month"),
    endDate: initialMonth.endOf("month"),
  });
  const [employeeData, setEmployeeData] = useState([]);
  const [totalsData, setTotalsData] = useState(null);
  const [issueType, setIssueType] = useState(null);
  const issueTypeOptions = [
    { label: "All", value: null },
    { label: "Bug", value: ISSUETYPE.BUG },
    { label: "Task", value: ISSUETYPE.TASK },
    { label: "Meeting", value: ISSUETYPE.MEETING },
    { label: "Testing", value: ISSUETYPE.TESTING },
    { label: "Designing", value: ISSUETYPE.DESIGNING },
    { label: "Others", value: ISSUETYPE.OTHERS },
    { label: "Deployment", value: ISSUETYPE.DEPLOYMENT },
  ];

  const [payableFilter, setPayableFilter] = useState("All");
  const payableOptions = [
    { label: "All", value: "All" },
    { label: "Payable", value: "true" },
    { label: "Unpayable", value: "false" },
  ];

  const [queryParam, setQueryParam] = useState({
    search: "",
    page: 1,
    limit: 10,
    sort: "",
  });

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const res = await getUser({ isEnabled: true });
        const configData = res.data.data.map((e) => ({
          ...e,
          label: e?.firstName + (e?.lastName ? " " + e.lastName : ""),
        }));
        setEmployeeData([{ label: "All", _id: "All" }, ...configData]);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };
    fetchEmployeeData();
  }, []);

  const currentYear = new Date().getFullYear();
  const fetchUserReport = async () => {
    setLoading(true);
    try {
      const res = await getUserReport({
        ...queryParam,
        startDate: dateRange?.startDate
          ? dayjs(dateRange.startDate).toISOString()
          : null,
        endDate: dateRange?.endDate
          ? dayjs(dateRange.endDate).toISOString()
          : null,
        issueType: issueType,
        payable: payableFilter === "All" ? null : payableFilter,
      });
      const users = res.data;
      const totalRow = users.find((u) => u.userName === "Total");
      const usersWithoutTotal = users.filter((u) => u.userName !== "Total");

      const allProjectNames = new Set();
      users.forEach((user) => {
        user.projects.forEach((project) => {
          allProjectNames.add(project.projectName);
        });
      });

      setProjectNames([...allProjectNames]);
      setData(users);
      setFilteredData(usersWithoutTotal);
      setTotalsData(totalRow);
    } catch (error) {
      console.error("Error fetching user report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserReport();
  }, [queryParam, dateRange, issueType, payableFilter]);

  const handleDateRangeChange = useCallback((value) => {
    const [start, end] = value || [];
    setDateRange({
      startDate: start ? dayjs(start).startOf("day") : null,
      endDate: end ? dayjs(end).endOf("day") : null,
    });
  }, []);

  useEffect(() => {
    const usersWithoutTotal = data.filter((u) => u.userName !== "Total");
    if (!selectedEmployeeId || selectedEmployeeId === "All") {
      setFilteredData(usersWithoutTotal);
    } else {
      const filtered = usersWithoutTotal.filter(
        (user) => user.userId === selectedEmployeeId
      );
      setFilteredData(filtered);
    }
  }, [selectedEmployeeId, data]);

  const handleEmployeeChange = (e, v) => {
    setSelectedEmployeeId(v?._id);
  };

  const handleCellClick = (row, column) => {
    // Only handle clicks on project columns (not userName or Total)
    if (column.isClickable && row[column.id] !== "0.00") {
      // Find the user data to get userId
      const userData = filteredData.find((u) => u.userName === row.userName);
      if (!userData) return;

      // Find the project data to get projectId
      const projectData = userData.projects.find(
        (p) => p.projectName === column.id
      );
      if (!projectData) return;

      setDialogData({
        projectId: projectData.projectId,
        projectName: column.id,
        userId: userData.userId,
        issueType: issueType,
        // Pass date range for filtering
        startDate: dateRange.startDate ? dateRange.startDate.toISOString() : null,
        endDate: dateRange.endDate ? dateRange.endDate.toISOString() : null,
      });
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogData({
      projectId: null,
      projectName: "",
      userId: null,
      issueType: null,
      startDate: null,
      endDate: null,
    });
  };

  const columns = [
    { id: "userName", label: "Name" },
    ...projectNames.map((name) => ({
      id: name,
      label: name,
      isTotal: true,
      totalType: "time",
      isClickable: true, // Make project columns clickable
    })),
    {
      id: "Total",
      label: "Total Hours",
      isTotal: true,
      isTotalColumn: true,
      totalType: "time",
    },
  ];

  const finalTableData = filteredData.map((user) => {
    const row = {
      userName: user.userName,
    };
    let totalUserMinutes = 0;
    projectNames.forEach((name) => {
      const project = user.projects.find((p) => p.projectName === name);
      const totalHoursAsFloat = project?.totalMinutes || 0;

      const hours = Math.floor(totalHoursAsFloat);
      const mins = Math.round((totalHoursAsFloat % 1) * 100);
      totalUserMinutes += hours * 60 + mins;

      row[name] = `${hours}.${mins.toString().padStart(2, "0")}`;
    });

    const totalHours = Math.floor(totalUserMinutes / 60);
    const totalMins = totalUserMinutes % 60;
    row.Total = `${totalHours}.${totalMins.toString().padStart(2, "0")}`;

    return row;
  });

  const tableConfig = {
    isAction: false,
    isEdit: false,
    isMore: false,
    isAdmin: false,
  };

  return (
    <div className="app-container">
      <Grid container spacing={4} mb={2}>
        <Grid item>
          <RangePicker
            value={[
              dateRange.startDate ? dayjs(dateRange.startDate) : null,
              dateRange.endDate ? dayjs(dateRange.endDate) : null,
            ]}
            onChange={(dates) => {
              const [start, end] = dates || [];
              setDateRange({
                startDate: start ? dayjs(start).startOf("day") : null,
                endDate: end ? dayjs(end).endOf("day") : null,
              });
            }}
            className="datePicker w-100"
            format={"DD-MM-YYYY"}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            value={
              employeeData.find((e) => e._id === selectedEmployeeId) || null
            }
            options={employeeData}
            onChange={handleEmployeeChange}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select Employee" />
            )}
            sx={{ width: 200 }}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            value={
              issueTypeOptions.find((opt) => opt.value === issueType) ||
              issueTypeOptions[0]
            }
            options={issueTypeOptions}
            onChange={(e, v) => setIssueType(v?.value)}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select Issue Type" />
            )}
            sx={{ width: 200 }}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            options={payableOptions}
            getOptionLabel={(option) => option.label}
            value={payableOptions.find((opt) => opt.value === payableFilter) || payableOptions[0]}
            onChange={(e, val) => setPayableFilter(val ? val.value : "All")}
            renderInput={(params) => <TextField {...params} label="Payable" />}
            sx={{ width: 200 }}
            disableClearable
          />
        </Grid>
      </Grid>

      <AdvancedTable
        columns={columns}
        data={finalTableData}
        tableConfig={tableConfig}
        isPagination={false}
        openLoader={loading}
        onCellClick={handleCellClick}
      />

      <ClientTaskDetailsDialog
        open={dialogOpen}
        handleClose={handleDialogClose}
        data={dialogData}
      />
    </div>
  );
};

export default CandidateReportTable;
